'use client';

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { useFocusTrap } from 'hooks/dom/useFocusTrap';
import { CartCross } from 'assets/icons/CartCross';
import type { BestBuyPickupStore } from './api';
import { StoreRowContent } from './StoreRowContent';
import rowStyles from './BuyPagePickup.module.css';
import styles from './BuyPageStoresModal.module.css';

type Props = {
  stores: BestBuyPickupStore[];
  chosenId: string | null;
  zip: string;
  sku: string | null;
  /** A store was picked (also the already chosen one): select and close. */
  onPick: (store: BestBuyPickupStore) => void;
  onShip: () => void;
  /** Esc, the close button or the scrim. Stable (useCallback): the focus
   * trap re-engages whenever it changes. */
  onClose: () => void;
};

/** Every Best Buy store for the ZIP, opened from "Show all N stores" on the
 * buy page. Rows are buttons, not radios: arrow keys on a radio group select
 * as they move, which would pick a store and close the dialog on the first
 * press. The parent owns focus on close (restoreFocus off). */
export const BuyPageStoresModal = ({
  stores,
  chosenId,
  zip,
  sku,
  onPick,
  onShip,
  onClose,
}: Props) => {
  const t = useTranslations('ring-pro');
  const tCommon = useTranslations('common');
  const titleId = useId();
  const summaryId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const pressedOnScrim = useRef(false);

  useFocusTrap(dialogRef, {
    onEscape: onClose,
    restoreFocus: false,
    contain: true,
    initialFocus: (root) =>
      root.querySelector<HTMLElement>('[aria-pressed="true"]') ??
      root.querySelector<HTMLElement>('[data-bb-modal-store]:not(:disabled)'),
  });

  // Body only: locking <html> too breaks the buy page's sticky gallery
  // (see components/shared/Modal.tsx).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const available = stores.filter((store) => store.allItemsAvailable);
  const unavailable = stores.filter((store) => !store.allItemsAvailable);

  const renderRow = (store: BestBuyPickupStore) => {
    const selectable = store.allItemsAvailable;
    const isChosen = chosenId === store.location_id;
    return (
      <li key={store.location_id}>
        <button
          type="button"
          className={`${rowStyles.store} ${styles.row} ${
            isChosen ? rowStyles.storeActive : ''
          } ${selectable ? '' : rowStyles.storeDisabled}`}
          aria-pressed={isChosen}
          disabled={!selectable}
          onClick={() => onPick(store)}
          data-bb-modal-store
          data-testid={`bb-modal-store-${store.location_id}`}
        >
          <StoreRowContent
            store={store}
            sku={sku}
            afterName={
              isChosen ? (
                <svg
                  className={styles.check}
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="10" cy="10" r="10" fill="#112baf" />
                  <path
                    d="M6 10.4l2.6 2.6L14 7.6"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null
            }
          />
        </button>
      </li>
    );
  };

  return createPortal(
    <div
      className={styles.overlay}
      onPointerDown={(event) => {
        pressedOnScrim.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        // Only a press that starts and ends on the scrim closes: a text
        // selection dragged out of the sheet must not.
        if (pressedOnScrim.current && event.target === event.currentTarget) {
          onClose();
        }
        pressedOnScrim.current = false;
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        // Clicks on blank sheet space or a disabled row land here rather
        // than on <body>; the trap (contain) sends Tab from here to the
        // first or last control.
        tabIndex={-1}
        aria-labelledby={titleId}
        aria-describedby={summaryId}
        data-testid="bb-stores-modal"
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title} id={titleId}>
              {t('ring-pro.bestBuyPickup.modalTitle', { zip })}
            </h2>
            <p className={styles.summary} id={summaryId}>
              {t('ring-pro.bestBuyPickup.modalSummary', {
                available: available.length,
                count: stores.length,
              })}
            </p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={tCommon('modal.button.ariaLabel.close')}
            data-testid="bb-stores-modal-close"
          >
            <span aria-hidden="true">
              <CartCross />
            </span>
          </button>
        </div>
        <div className={styles.body}>
          <ul className={styles.list}>{available.map(renderRow)}</ul>
          {unavailable.length > 0 ? (
            <>
              <p className={styles.groupLabel}>
                {t('ring-pro.bestBuyPickup.modalUnavailable')}
              </p>
              <ul className={styles.list}>{unavailable.map(renderRow)}</ul>
            </>
          ) : null}
          <button
            type="button"
            className={styles.ship}
            onClick={onShip}
            data-testid="bb-stores-modal-ship"
          >
            {t('ring-pro.bestBuyPickup.shipInstead')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
